import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Activity, Shield, Users, TrendingUp, KeyRound, ArrowLeft, Sparkles, Zap, Brain, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECURITY_QUESTIONS = [
  "What is your mother's maiden name?",
  "What was the name of your first pet?",
  "What city were you born in?",
  "What is your favorite book?",
  "What was your childhood nickname?",
  "What is the name of your favorite teacher?"
];

export default function Login() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);
  
  const [resetStep, setResetStep] = useState(1);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ 
    name: '', email: '', password: '', confirmPassword: '',
    securityQuestion: '', securityAnswer: ''
  });
  const [resetForm, setResetForm] = useState({
    email: '', securityQuestion: '', securityAnswer: '',
    newPassword: '', confirmPassword: ''
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        login(data.token, data.user);
        toast.success('Welcome back!', { description: `Signed in as ${data.user.name}` });
        navigate('/', { replace: true });
      } else {
        toast.error('Login failed', { description: data.detail || 'Invalid credentials' });
      }
    } catch (error) {
      toast.error('Connection error', { description: 'Unable to connect to server' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (registerForm.password !== registerForm.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (registerForm.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (!registerForm.securityQuestion || !registerForm.securityAnswer) {
      toast.error('Please set a security question and answer for password recovery');
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: registerForm.name,
          email: registerForm.email,
          password: registerForm.password,
          role: 'staff',
          security_question: registerForm.securityQuestion,
          security_answer: registerForm.securityAnswer
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        login(data.token, data.user);
        toast.success('Account created!', { description: 'Welcome to CareLens' });
        navigate('/', { replace: true });
      } else {
        toast.error('Registration failed', { description: data.detail || 'Could not create account' });
      }
    } catch (error) {
      toast.error('Connection error', { description: 'Unable to connect to server' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (resetStep === 1) {
      setIsLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/auth/password-reset/request`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: resetForm.email })
        });
        
        const data = await response.json();
        
        if (response.ok) {
          setResetForm({ ...resetForm, securityQuestion: data.security_question });
          setResetStep(2);
        } else {
          toast.error('Error', { description: data.detail || 'Could not find account' });
        }
      } catch (error) {
        toast.error('Connection error');
      } finally {
        setIsLoading(false);
      }
    } else if (resetStep === 2) {
      if (resetForm.newPassword !== resetForm.confirmPassword) {
        toast.error('Passwords do not match');
        return;
      }
      if (resetForm.newPassword.length < 6) {
        toast.error('Password must be at least 6 characters');
        return;
      }
      
      setIsLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/auth/password-reset/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: resetForm.email,
            security_answer: resetForm.securityAnswer,
            new_password: resetForm.newPassword
          })
        });
        
        const data = await response.json();
        
        if (response.ok) {
          toast.success('Password reset successful!', { description: 'You can now login with your new password' });
          setShowForgotPassword(false);
          setResetStep(1);
          setResetForm({ email: '', securityQuestion: '', securityAnswer: '', newPassword: '', confirmPassword: '' });
        } else {
          toast.error('Reset failed', { description: data.detail || 'Incorrect security answer' });
        }
      } catch (error) {
        toast.error('Connection error');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const features = [
    { icon: Brain, title: 'AI Intelligence', desc: 'GPT-powered insights & predictions' },
    { icon: Shield, title: 'CMS Compliant', desc: 'CHAP & survey-ready compliance' },
    { icon: TrendingUp, title: 'Predictive Analytics', desc: 'Forecast trends before they happen' },
    { icon: Zap, title: 'CareFlow Engine', desc: 'Real-time operational intelligence' }
  ];

  return (
    <div className="min-h-screen flex overflow-hidden">
      {/* Left Panel - Futuristic Brand Visual */}
      <div className="hidden lg:flex lg:w-[58%] relative">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1656614333741-9d1a61e3934a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwzfHxwZWFjZWZ1bCUyMGFic3RyYWN0JTIwZmxvd2luZyUyMHdhdGVyJTIwb3IlMjB3aW5kJTIwdGVhbCUyMGJsdWUlMjBiYWNrZ3JvdW5kJTIwaGlnaCUyMHF1YWxpdHl8ZW58MHx8fHwxNzczMDE0Mzc1fDA&ixlib=rb-4.1.0&q=85')`
          }}
        />
        
        {/* Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#00b0ab]/90 via-[#008a86]/80 to-[#0f172a]/95" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0f172a]/60 via-transparent to-transparent" />
        
        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundSize: '60px 60px',
          backgroundImage: `
            linear-gradient(to right, rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(255,255,255,0.1) 1px, transparent 1px)
          `
        }} />
        
        {/* Animated Glow Orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#00F0FF]/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-[#00b0ab]/30 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        
        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          {/* Logo & Title */}
          <div className="animate-fade-in">
            <div className="flex items-center gap-4 mb-6">
              <div className="h-16 w-16 rounded-2xl bg-white/10 backdrop-blur-xl flex items-center justify-center border border-white/20 shadow-glow">
                <Activity className="h-9 w-9 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-white font-heading tracking-tight">
                  Goodwin Hospice
                </h1>
                <p className="text-white/70 text-lg font-medium mt-1">CareLens Intelligence Platform</p>
              </div>
            </div>
            
            <p className="text-white/60 text-xl max-w-lg leading-relaxed mt-8">
              The future of hospice operations. AI-powered analytics meet compassionate care.
            </p>
          </div>
          
          {/* Feature Cards */}
          <div className="space-y-4 my-12">
            {features.map((feature, index) => (
              <div 
                key={index} 
                className="group flex items-center gap-5 p-5 rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 
                           hover:bg-white/10 hover:border-[#00F0FF]/30 transition-all duration-300 animate-slide-right cursor-default"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="h-14 w-14 rounded-xl bg-gradient-to-br from-[#00F0FF]/20 to-[#00b0ab]/20 
                               flex items-center justify-center border border-white/10 group-hover:border-[#00F0FF]/40 
                               group-hover:shadow-glow transition-all duration-300">
                  <feature.icon className="h-7 w-7 text-[#00F0FF]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-white text-lg">{feature.title}</h3>
                  <p className="text-sm text-white/60 mt-0.5">{feature.desc}</p>
                </div>
                <ChevronRight className="h-5 w-5 text-white/30 group-hover:text-[#00F0FF] group-hover:translate-x-1 transition-all duration-300" />
              </div>
            ))}
          </div>
          
          {/* Footer */}
          <div className="animate-fade-in" style={{ animationDelay: '400ms' }}>
            <div className="flex items-center gap-2 mb-2">
              <div className="h-2 w-2 rounded-full bg-[#00F0FF] animate-pulse shadow-glow" />
              <span className="text-white/70 text-sm font-medium">System Online</span>
            </div>
            <p className="text-white/40 text-sm">
              © 2026 Goodwin Hospice • CMS & CHAP Compliant • HIPAA Secure
            </p>
          </div>
        </div>
      </div>
      
      {/* Right Panel - Auth Forms */}
      <div className="flex-1 lg:w-[42%] flex items-center justify-center p-6 sm:p-8 lg:p-12 
                      bg-gradient-to-br from-slate-50 via-white to-[#f0faf9] 
                      dark:from-[#0B1114] dark:via-[#0f172a] dark:to-[#0B1114]
                      relative overflow-hidden">
        {/* Background Grid Pattern */}
        <div className="absolute inset-0 grid-pattern opacity-50" />
        
        {/* Subtle Glow */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#00b0ab]/5 dark:bg-[#00F0FF]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#00b0ab]/5 dark:bg-[#00F0FF]/5 rounded-full blur-3xl" />
        
        <div className="w-full max-w-md relative z-10">
          {/* Mobile Header */}
          <div className="lg:hidden text-center mb-8 animate-fade-in">
            <div className="flex items-center justify-center gap-3 mb-2">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#00b0ab] to-[#008a86] flex items-center justify-center shadow-lg shadow-[#00b0ab]/20">
                <Activity className="h-7 w-7 text-white" />
              </div>
              <span className="text-2xl font-bold font-heading text-slate-900 dark:text-white">Goodwin Hospice</span>
            </div>
            <p className="text-sm text-muted-foreground">CareLens Intelligence Platform</p>
          </div>
          
          {/* Auth Card - Glass Design */}
          <div className="glass-card p-8 animate-scale-in">
            <div className="space-y-2 text-center mb-8">
              <h2 className="text-2xl font-bold font-heading text-foreground">
                {activeTab === 'login' ? 'Welcome Back' : 'Create Account'}
              </h2>
              <p className="text-muted-foreground">
                {activeTab === 'login' 
                  ? 'Sign in to access your intelligence dashboard' 
                  : 'Get started with quality improvement'
                }
              </p>
            </div>
            
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-8 bg-muted/50 p-1 rounded-xl">
                <TabsTrigger 
                  value="login" 
                  data-testid="login-tab"
                  className="rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 data-[state=active]:shadow-sm transition-all"
                >
                  Sign In
                </TabsTrigger>
                <TabsTrigger 
                  value="register" 
                  data-testid="register-tab"
                  className="rounded-lg data-[state=active]:bg-white dark:data-[state=active]:bg-slate-800 data-[state=active]:shadow-sm transition-all"
                >
                  Register
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="login" className="space-y-5">
                <form onSubmit={handleLogin} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="login-email" className="text-sm font-medium">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="you@hospice.org"
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      required
                      data-testid="login-email-input"
                      className="h-12 bg-white/50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 
                                 focus:border-[#00b0ab] focus:ring-[#00b0ab]/20 transition-all"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="login-password" className="text-sm font-medium">Password</Label>
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        className="text-sm text-[#00b0ab] hover:text-[#008a86] font-medium transition-colors"
                        data-testid="forgot-password-link"
                      >
                        Forgot password?
                      </button>
                    </div>
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="••••••••"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      required
                      data-testid="login-password-input"
                      className="h-12 bg-white/50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 
                                 focus:border-[#00b0ab] focus:ring-[#00b0ab]/20 transition-all"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-12 bg-gradient-to-r from-[#00b0ab] to-[#008a86] hover:from-[#008a86] hover:to-[#006965]
                               text-white font-semibold rounded-xl shadow-lg shadow-[#00b0ab]/25 
                               hover:shadow-[#00b0ab]/40 transition-all duration-300"
                    disabled={isLoading}
                    data-testid="login-submit-btn"
                  >
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Signing in...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        Sign In
                        <ChevronRight className="h-4 w-4" />
                      </span>
                    )}
                  </Button>
                </form>
              </TabsContent>
              
              <TabsContent value="register" className="space-y-4">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-name" className="text-sm font-medium">Full Name</Label>
                    <Input
                      id="register-name"
                      type="text"
                      placeholder="Jane Smith"
                      value={registerForm.name}
                      onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                      required
                      data-testid="register-name-input"
                      className="h-11 bg-white/50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 
                                 focus:border-[#00b0ab] focus:ring-[#00b0ab]/20 transition-all"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email" className="text-sm font-medium">Email</Label>
                    <Input
                      id="register-email"
                      type="email"
                      placeholder="you@hospice.org"
                      value={registerForm.email}
                      onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                      required
                      data-testid="register-email-input"
                      className="h-11 bg-white/50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 
                                 focus:border-[#00b0ab] focus:ring-[#00b0ab]/20 transition-all"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="register-password" className="text-sm font-medium">Password</Label>
                      <Input
                        id="register-password"
                        type="password"
                        placeholder="••••••••"
                        value={registerForm.password}
                        onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                        required
                        data-testid="register-password-input"
                        className="h-11 bg-white/50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 
                                   focus:border-[#00b0ab] focus:ring-[#00b0ab]/20 transition-all"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-confirm" className="text-sm font-medium">Confirm</Label>
                      <Input
                        id="register-confirm"
                        type="password"
                        placeholder="••••••••"
                        value={registerForm.confirmPassword}
                        onChange={(e) => setRegisterForm({ ...registerForm, confirmPassword: e.target.value })}
                        required
                        data-testid="register-confirm-input"
                        className="h-11 bg-white/50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 
                                   focus:border-[#00b0ab] focus:ring-[#00b0ab]/20 transition-all"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Security Question</Label>
                    <Select 
                      value={registerForm.securityQuestion}
                      onValueChange={(value) => setRegisterForm({ ...registerForm, securityQuestion: value })}
                    >
                      <SelectTrigger data-testid="security-question-select" className="h-11 bg-white/50 dark:bg-slate-800/50">
                        <SelectValue placeholder="Select a security question" />
                      </SelectTrigger>
                      <SelectContent>
                        {SECURITY_QUESTIONS.map((q, i) => (
                          <SelectItem key={i} value={q}>{q}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="security-answer" className="text-sm font-medium">Security Answer</Label>
                    <Input
                      id="security-answer"
                      type="text"
                      placeholder="Your answer"
                      value={registerForm.securityAnswer}
                      onChange={(e) => setRegisterForm({ ...registerForm, securityAnswer: e.target.value })}
                      data-testid="security-answer-input"
                      className="h-11 bg-white/50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 
                                 focus:border-[#00b0ab] focus:ring-[#00b0ab]/20 transition-all"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-12 bg-gradient-to-r from-[#00b0ab] to-[#008a86] hover:from-[#008a86] hover:to-[#006965]
                               text-white font-semibold rounded-xl shadow-lg shadow-[#00b0ab]/25 
                               hover:shadow-[#00b0ab]/40 transition-all duration-300"
                    disabled={isLoading}
                    data-testid="register-submit-btn"
                  >
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Creating account...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        Create Account
                        <Sparkles className="h-4 w-4" />
                      </span>
                    )}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </div>
          
          {/* Security Badge */}
          <div className="mt-6 text-center animate-fade-in" style={{ animationDelay: '200ms' }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100/80 dark:bg-slate-800/50 border border-slate-200/50 dark:border-slate-700/50">
              <Shield className="h-4 w-4 text-[#00b0ab]" />
              <span className="text-xs text-muted-foreground font-medium">256-bit SSL Encrypted</span>
            </div>
          </div>
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={(open) => {
        setShowForgotPassword(open);
        if (!open) {
          setResetStep(1);
          setResetForm({ email: '', securityQuestion: '', securityAnswer: '', newPassword: '', confirmPassword: '' });
        }
      }}>
        <DialogContent className="sm:max-w-md glass-card border-slate-200 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading">
              <KeyRound className="h-5 w-5 text-[#00b0ab]" />
              Reset Password
            </DialogTitle>
            <DialogDescription>
              {resetStep === 1 
                ? "Enter your email to receive your security question" 
                : "Answer your security question to reset your password"
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {resetStep === 1 && (
              <div className="space-y-2">
                <Label htmlFor="reset-email">Email</Label>
                <Input
                  id="reset-email"
                  type="email"
                  placeholder="you@hospice.org"
                  value={resetForm.email}
                  onChange={(e) => setResetForm({ ...resetForm, email: e.target.value })}
                  data-testid="reset-email-input"
                  className="h-11 bg-white/50 dark:bg-slate-800/50"
                />
              </div>
            )}
            
            {resetStep === 2 && (
              <>
                <div className="p-4 bg-[#00b0ab]/10 dark:bg-[#00F0FF]/10 rounded-xl border border-[#00b0ab]/20">
                  <p className="text-sm font-medium text-[#00b0ab] dark:text-[#00F0FF]">
                    Security Question:
                  </p>
                  <p className="text-sm mt-1 text-foreground">{resetForm.securityQuestion}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reset-answer">Your Answer</Label>
                  <Input
                    id="reset-answer"
                    type="text"
                    placeholder="Enter your answer"
                    value={resetForm.securityAnswer}
                    onChange={(e) => setResetForm({ ...resetForm, securityAnswer: e.target.value })}
                    data-testid="reset-answer-input"
                    className="h-11 bg-white/50 dark:bg-slate-800/50"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="reset-new-password">New Password</Label>
                    <Input
                      id="reset-new-password"
                      type="password"
                      placeholder="••••••••"
                      value={resetForm.newPassword}
                      onChange={(e) => setResetForm({ ...resetForm, newPassword: e.target.value })}
                      data-testid="reset-new-password-input"
                      className="h-11 bg-white/50 dark:bg-slate-800/50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reset-confirm-password">Confirm</Label>
                    <Input
                      id="reset-confirm-password"
                      type="password"
                      placeholder="••••••••"
                      value={resetForm.confirmPassword}
                      onChange={(e) => setResetForm({ ...resetForm, confirmPassword: e.target.value })}
                      data-testid="reset-confirm-password-input"
                      className="h-11 bg-white/50 dark:bg-slate-800/50"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
          
          <DialogFooter className="flex gap-2">
            {resetStep === 2 && (
              <Button 
                variant="ghost" 
                onClick={() => setResetStep(1)}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" /> Back
              </Button>
            )}
            <Button 
              onClick={handleForgotPassword}
              disabled={isLoading}
              data-testid="reset-submit-btn"
              className="bg-gradient-to-r from-[#00b0ab] to-[#008a86] hover:from-[#008a86] hover:to-[#006965]"
            >
              {isLoading ? 'Processing...' : resetStep === 1 ? 'Continue' : 'Reset Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
